import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr, CONF, REGISTER_FIELDS, DOC_TYPE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocumentViewer from "@/components/DocumentViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Lock, LockOpen, Check, X, Save, Bot, ScanText, AlertTriangle, CheckCircle, Clock, Hash } from "lucide-react";

const LONG = ["uraian_informasi", "keterangan"];

const KONDISI_OPTIONS = ["Baik", "Rusak Ringan", "Rusak Sedang", "Rusak Berat"];
const PERKEMBANGAN_OPTIONS = ["Asli", "Salinan", "Tembusan", "Pertinggal", "Gandaan"];

function Field({ label, value }) {
  return (
    <div>
      <p className="uppercase text-[9px] tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="text-xs">{value || "-"}</p>
    </div>
  );
}

export default function ReviewPanel({ projectId, record, onChanged, canReview = true, lockTimeout = 30, bidangOptions = [] }) {
  const { user } = useAuth();
  const [rec, setRec] = useState(record);
  const [form, setForm] = useState(record.data);
  const [detail, setDetail] = useState(null);
  const [blob, setBlob] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null);
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 20000); return () => clearInterval(t); }, []);

  useEffect(() => { setRec(record); setForm(record.data); }, [record.id]);

  useEffect(() => {
    if (["pending_review", "in_review"].includes(rec.status)) {
      api.get(`/projects/${projectId}/records/${rec.id}/validate`).then((r) => setValidation(r.data)).catch(() => {});
    }
  }, [rec.id, rec.version, rec.status, projectId]);

  useEffect(() => {
    let url;
    api.get(`/projects/${projectId}/documents/${record.document_id}`).then((r) => setDetail(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/documents/${record.document_id}/file`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setBlob({ url, type: r.data.type }); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [record.document_id, projectId]);

  const [masterOpts, setMasterOpts] = useState([]);
  useEffect(() => {
    api.get(`/projects/${projectId}/master-data/unified`).then((r) => setMasterOpts(r.data.items || [])).catch(() => {});
  }, [projectId]);
  const applyKode = (kode) => {
    const row = masterOpts.find((o) => o.kode_klasifikasi === kode) || {};
    setForm((f) => ({
      ...f, kode_klasifikasi: kode,
      retensi_aktif: row.retensi_aktif ?? f.retensi_aktif,
      retensi_inaktif: row.retensi_inaktif ?? f.retensi_inaktif,
      nasib_akhir: row.nasib_akhir ?? f.nasib_akhir,
      hak_akses_internal: row.hak_akses_internal ?? f.hak_akses_internal,
      hak_akses_eksternal: row.hak_akses_eksternal ?? f.hak_akses_eksternal,
    }));
  };

  const mine = rec.locked_by === user?.id;
  const ai = rec.ai_result || {};
  const remainMin = rec.locked_at ? Math.round(lockTimeout - (Date.now() - new Date(rec.locked_at).getTime()) / 60000) : lockTimeout;

  const act = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  const claim = () => act(async () => {
    const { data } = await api.post(`/projects/${projectId}/records/${rec.id}/claim`);
    setRec(data); setForm(data.data); toast.success("Record diklaim untuk review");
    onChanged?.();
  });
  const release = () => act(async () => {
    await api.post(`/projects/${projectId}/records/${rec.id}/release`);
    toast.info("Lock dilepas"); onChanged?.();
    setRec({ ...rec, status: "pending_review", locked_by: null });
  });
  const save = () => act(async () => {
    const { data } = await api.patch(`/projects/${projectId}/records/${rec.id}`, { version: rec.version, data: form });
    setRec(data); setForm(data.data); toast.success("Metadata disimpan");
  });
  const extend = () => act(async () => {
    const { data } = await api.post(`/projects/${projectId}/records/${rec.id}/extend`);
    setRec({ ...rec, locked_at: data.locked_at }); toast.success("Sesi review diperpanjang");
  });
  const approve = () => act(async () => {
    const { data } = await api.post(`/projects/${projectId}/records/${rec.id}/approve`, { version: rec.version, data: form });
    setRec(data); toast.success("Record disetujui → Daftar Arsip"); onChanged?.();
  });
  const doReject = () => act(async () => {
    await api.post(`/projects/${projectId}/records/${rec.id}/reject`, { reason, version: rec.version });
    setRejectOpen(false); toast.success("Record ditolak"); onChanged?.();
    setRec({ ...rec, status: "rejected" });
  });

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      {/* Left: document + AI */}
      <div className="space-y-4">
        <DocumentViewer 
          url={blob?.url} 
          type={blob?.type} 
          filename={detail?.document?.filename} 
          subfolder={rec.sub_project || rec.data?.bidang || detail?.document?.sub_project || detail?.document?.bidang} 
        />

        {rec.duplicate_info && (
          <Card className={`p-3 border-l-4 ${rec.duplicate_info.status === "approved" ? "border-l-red-500 bg-red-500/5" : "border-l-amber-500 bg-amber-500/5"}`} data-testid="duplicate-warning">
            <p className={`text-xs font-semibold flex items-center gap-1.5 ${rec.duplicate_info.status === "approved" ? "text-red-600" : "text-amber-600"}`}>
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {rec.duplicate_info.status === "approved" ? "Kemungkinan duplikat dari arsip yang sudah disetujui" : "Kemungkinan duplikat dari record lain"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              File identik (hash sama) ditemukan pada Nomor Berkas {rec.duplicate_info.nomor_berkas || "-"}
              {rec.duplicate_info.status === "approved" && rec.duplicate_info.approved_by ? ` — disetujui oleh ${rec.duplicate_info.approved_by}` : ""}
              {rec.duplicate_info.status === "approved" && rec.duplicate_info.approved_at ? ` pada ${new Date(rec.duplicate_info.approved_at).toLocaleString("id-ID")}` : ""}.
              Periksa kembali sebelum menyetujui — pertimbangkan menolak record ini bila memang duplikat.
            </p>
          </Card>
        )}

        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><Bot className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Analisis AI</span></div>
            <Badge className={`border ${CONF[rec.confidence_level] || CONF.LOW}`} variant="outline">
              {rec.confidence_level} · {rec.confidence}%
            </Badge>
          </div>
          {rec.needs_human_review && (
            <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-500/10 rounded-md p-2 mb-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> Perlu verifikasi manusia — periksa kecocokan master data.
            </div>
          )}
          {rec.ocr_quality && (
            <p className="text-[11px] text-muted-foreground mb-1">Kualitas OCR: <span className="font-semibold">{rec.ocr_quality}</span>{rec.ocr_quality === "LOW" || rec.ocr_quality === "FAILED" ? " — HUMAN REVIEW REQUIRED" : ""}</p>
          )}
          <p className="text-xs text-muted-foreground">{ai.explanation}</p>
          <p className="text-[11px] text-muted-foreground mt-2">Model: {ai.ai_model} · Match: {ai.matched_classification || "-"}</p>
        </Card>

        <Card className="p-4" data-testid="ai-analysis-card">
          <div className="flex items-center gap-2 mb-2"><Bot className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Analisis Dokumen AI</span></div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Field label="Jenis Dokumen" value={DOC_TYPE_LABELS[rec.document_type || ai.document_type] || ai.document_type} />
            <Field label="Perihal" value={ai.subject} />
            <Field label="Organisasi" value={ai.organization} />
            <Field label="Nomor Dokumen" value={ai.document_number} />
            <Field label="Tanggal" value={ai.dates} />
            <Field label="Klasifikasi" value={ai.classification_name} />
          </div>
          {ai.context && <p className="text-[11px] text-muted-foreground mt-2 italic">{ai.context}</p>}
          {(rec.number_candidates || ai.number_candidates || []).length > 0 && (
            <div className="mt-3" data-testid="number-candidates">
              <p className="uppercase text-[9px] tracking-wider font-semibold text-muted-foreground mb-1 flex items-center gap-1"><Hash className="h-3 w-3" /> Kandidat Nomor Dokumen</p>
              <div className="space-y-1">
                {(rec.number_candidates || ai.number_candidates || []).map((c, i) => (
                  <button key={i} type="button" disabled={!mine}
                    onClick={() => setForm({ ...form, nomor_berkas: c.value })}
                    className="w-full text-left flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1 hover:bg-accent/40 disabled:opacity-60 transition-colors"
                    data-testid={`num-candidate-${i}`}>
                    <span className="text-xs font-medium tabular-nums">{c.value || "-"}</span>
                    <span className="text-[10px] text-muted-foreground">{c.label ? `${c.label} · ` : ""}{c.confidence ? `${Math.round(c.confidence)}%` : ""}{c.source_page ? ` · Hal ${c.source_page}` : ""}</span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Klik untuk mengisi ke Nomor Berkas (dapat diedit manual).</p>
            </div>
          )}
        </Card>

        <Card className="p-4 border-l-4 border-l-primary" data-testid="classification-card">
          <div className="flex items-center gap-2 mb-2"><Bot className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Klasifikasi AI (dari Uraian)</span></div>
          <div className="space-y-1.5 text-xs">
            <Field label="Uraian Informasi Arsip" value={form.uraian_informasi} />
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              <Field label="Kode Klasifikasi" value={form?.kode_klasifikasi || ai?.kode_klasifikasi || "-"} />
              <Field label="Uraian Klasifikasi" value={ai.classification_name} />
            </div>
            <Badge variant="outline" className={`text-[10px] border ${CONF[ai.confidence_level] || CONF.LOW}`} data-testid="classification-status">{ai.classification_status ? `${ai.classification_status} · ${ai.confidence || 0}%` : "Belum ada klasifikasi dua-tahap (record lama)"}</Badge>
            {ai.classification_reason && <p className="text-[11px] text-muted-foreground italic mt-1">Alasan: {ai.classification_reason}</p>}
          </div>
          {(ai.classification_candidates || []).length > 0 && (
            <div className="mt-3" data-testid="classification-candidates">
              <p className="uppercase text-[9px] tracking-wider font-semibold text-muted-foreground mb-1">Kandidat Klasifikasi</p>
              <div className="space-y-1">
                {ai.classification_candidates.map((c, i) => (
                  <button key={i} type="button" disabled={!mine} onClick={() => applyKode(c.kode_klasifikasi)}
                    className="w-full text-left flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1 hover:bg-accent/40 disabled:opacity-60 transition-colors"
                    data-testid={`class-candidate-${i}`}>
                    <span className="text-xs font-medium">{c.kode_klasifikasi} — {c.uraian_klasifikasi}</span>
                    <span className="text-[10px] text-muted-foreground">{c.confidence ? `${Math.round(c.confidence)}%` : ""}</span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Klik untuk memilih; retensi &amp; hak akses otomatis diperbarui dari master data.</p>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2"><ScanText className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">Hasil OCR</span></div>
          <pre className="text-[11px] whitespace-pre-wrap text-muted-foreground max-h-40 overflow-auto scrollbar-thin font-sans">{detail?.ocr?.text || "Tidak ada teks OCR."}</pre>
        </Card>
      </div>

      {/* Right: metadata form + actions */}
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Metadata Arsip</span>
              {(rec.sub_project || rec.data?.bidang) && (
                <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-600 border-blue-200">
                  📁 {rec.sub_project || rec.data?.bidang}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="text-[10px]">{rec.status}</Badge>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
            {REGISTER_FIELDS.map(([k, label]) => (
              <div key={k} className={`space-y-1.5 ${LONG.includes(k) ? "sm:col-span-2" : ""}`}>
                <Label className="text-[11px]">{label}</Label>
                {k === "kode_klasifikasi" ? (
                  <Select value={form.kode_klasifikasi || ""} onValueChange={applyKode} disabled={!mine}>
                    <SelectTrigger className="h-8 text-sm" data-testid="f-kode_klasifikasi"><SelectValue placeholder="Pilih dari master data" /></SelectTrigger>
                    <SelectContent>{masterOpts.map((o) => <SelectItem key={o.kode_klasifikasi} value={o.kode_klasifikasi}>{o.kode_klasifikasi} — {o.uraian}</SelectItem>)}</SelectContent>
                  </Select>
                ) : k === "kondisi_arsip" ? (
                    <Select value={form.kondisi_arsip || "Baik"} onValueChange={(val) => setForm({ ...form, kondisi_arsip: val })} disabled={!mine}>
                      <SelectTrigger className="h-8 text-sm" data-testid="f-kondisi_arsip"><SelectValue placeholder="Pilih Kondisi" /></SelectTrigger>
                      <SelectContent>
                        {KONDISI_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : k === "tingkat_perkembangan" ? (
                    <Select value={form.tingkat_perkembangan || "Asli"} onValueChange={(val) => setForm({ ...form, tingkat_perkembangan: val })} disabled={!mine}>
                      <SelectTrigger className="h-8 text-sm" data-testid="f-tingkat_perkembangan"><SelectValue placeholder="Pilih Tingkat Perkembangan" /></SelectTrigger>
                      <SelectContent>
                        {PERKEMBANGAN_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : LONG.includes(k) ? (
                  <Textarea rows={2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    disabled={!mine} data-testid={`f-${k}`} className="text-sm" />
                ) : (
                  <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    disabled={!mine} data-testid={`f-${k}`} className="h-8 text-sm" />
                )}
                {k === "nomor_berkas" && (
                  <p className="text-[10px] text-amber-600" data-testid="nomor-berkas-hint">
                    Angka tulisan tangan (pojok kanan atas). Dibaca AI bila jelas; jika kosong, isi manual.
                  </p>
                )}
                {k === "mL" && (
                  <p className="text-[10px] text-muted-foreground" data-testid="ml-hint">
                    Meter linear arsip — wajib diisi sebelum persetujuan.
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>

        {canReview && (
          <div className="flex flex-wrap gap-2">
            {rec.status === "pending_review" && (
              <Button onClick={claim} disabled={busy} className="rounded-full" data-testid="claim-btn"><Lock className="h-4 w-4 mr-1.5" /> Klaim Review</Button>
            )}
            {rec.status === "in_review" && !mine && (
              <p className="text-sm text-amber-600 flex items-center gap-1.5"><Lock className="h-4 w-4" /> Direview oleh {rec.locked_by_name}</p>
            )}
            {rec.status === "in_review" && mine && (
              <>
                {remainMin <= 5 && remainMin > 0 && (
                  <div className="w-full" data-testid="lock-expiry-warning">
                    <Card className="p-3 border-amber-300 bg-amber-500/10">
                      <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
                        <Clock className="h-4 w-4" /> Lock berakhir dalam ~{remainMin} menit. Perpanjang atau lepas untuk menghindari kehilangan sesi.
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={extend} disabled={busy} className="rounded-full" data-testid="extend-btn"><Clock className="h-4 w-4 mr-1.5" /> Lanjutkan Review</Button>
                        <Button size="sm" variant="outline" onClick={release} disabled={busy} className="rounded-full" data-testid="warn-release-btn"><LockOpen className="h-4 w-4 mr-1.5" /> Lepas</Button>
                      </div>
                    </Card>
                  </div>
                )}
                {validation && (
                  <div className="w-full" data-testid="validation-panel">
                    <Card className="p-3">
                      <p className="text-xs font-semibold mb-1 flex items-center gap-1.5">
                        {validation.valid ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                        Validasi Persetujuan
                      </p>
                      {validation.valid ? <p className="text-xs text-emerald-600">Semua syarat terpenuhi — siap disetujui.</p> :
                        <ul className="text-xs text-red-600 space-y-0.5 list-disc pl-4">{validation.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
                    </Card>
                  </div>
                )}
                <Button variant="outline" onClick={save} disabled={busy} className="rounded-full" data-testid="save-btn"><Save className="h-4 w-4 mr-1.5" /> Simpan</Button>
                <Button onClick={approve} disabled={busy || (validation && !validation.valid)} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><Check className="h-4 w-4 mr-1.5" /> Setujui</Button>
                <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={busy} className="rounded-full text-red-600 border-red-200" data-testid="reject-btn"><X className="h-4 w-4 mr-1.5" /> Tolak</Button>
                <Button variant="ghost" onClick={release} disabled={busy} className="rounded-full" data-testid="release-btn"><LockOpen className="h-4 w-4 mr-1.5" /> Lepas</Button>
              </>
            )}
            {rec.status === "approved" && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" variant="outline">Disetujui oleh {rec.approved_by}</Badge>}
            {rec.status === "rejected" && <Badge className="bg-red-500/15 text-red-600 border-red-500/30" variant="outline">Ditolak: {rec.reject_reason}</Badge>}
          </div>
        )}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tolak Record</DialogTitle></DialogHeader>
          <Textarea placeholder="Alasan penolakan..." value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Batal</Button>
            <Button onClick={doReject} disabled={!reason} className="bg-red-600 hover:bg-red-700" data-testid="reject-confirm">Tolak</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
